import type { Layer, LayerGroup, Map as LeafletMap } from "leaflet"
import { DataSetter, HeatmapRadiusStop } from "../utils/mapLayers"
import { splitTrackPointsAtMapEdges } from "./vesselTrackMapEdges"

type HeatmapRefs = {
	heatmapLayerId: string
	heatmapSourceId: string
}

type HeatmapLayerState = {
	requestId: number
	currentHeatmaps?: HeatmapRefs[]
	zoomHandler?: () => void
	lastRawPointGroups?: { latitude: number; longitude: number }[][]
}

type MaptilerMapLike = {
	getLayer: (id: string) => unknown
	getSource: (id: string) => unknown
	removeLayer: (id: string) => void
	removeSource: (id: string) => void
	isStyleLoaded?: () => boolean
}

type MaptilerLayerLike = Layer & {
	_maptilerMap?: MaptilerMapLike
	addHeatmap?: (args: {
		data: GeoJSON.FeatureCollection
		minzoom?: number
		maxzoom?: number
		opacity?: number
		radius?: number
		property?: string
		weight?: { propertyValue: number; value: number }[]
	}) => HeatmapRefs
}

type LayerGroupWithState = LayerGroup & {
	__vesselTrackHeatmapState?: HeatmapLayerState
}

const EMPTY_FEATURE_COLLECTION: GeoJSON.FeatureCollection = {
	type: "FeatureCollection",
	features: [],
}

const getOrCreateState = (layerGroup: LayerGroupWithState): HeatmapLayerState => {
	if (layerGroup.__vesselTrackHeatmapState === undefined) {
		layerGroup.__vesselTrackHeatmapState = { requestId: 0 }
	}
	return layerGroup.__vesselTrackHeatmapState
}

const findMaptilerLayer = (map: LeafletMap): MaptilerLayerLike | undefined => {
	let maptilerLayer: MaptilerLayerLike | undefined
	map.eachLayer((layer) => {
		if (maptilerLayer !== undefined) {
			return
		}
		const candidate = layer as unknown as MaptilerLayerLike
		if (typeof candidate.addHeatmap === "function") {
			maptilerLayer = candidate
		}
	})
	return maptilerLayer
}

const removeHeatmap = (maptilerMap: MaptilerMapLike | undefined, refs: HeatmapRefs | undefined) => {
	if (maptilerMap === undefined || refs === undefined) {
		return
	}
	if (maptilerMap.getLayer(refs.heatmapLayerId) !== undefined) {
		maptilerMap.removeLayer(refs.heatmapLayerId)
	}
	if (maptilerMap.getSource(refs.heatmapSourceId) !== undefined) {
		maptilerMap.removeSource(refs.heatmapSourceId)
	}
}

const getHeatmapStyle = (zoom: number) => {
	if (zoom <= 5) {
		return {
			radius: 6,
			opacity: 0.45,
			minWeight: 0.5,
			maxWeight: 0.9,
			maxStep: 20000,
			precision: 2,
		}
	}
	if (zoom <= 8) {
		return {
			radius: 10,
			opacity: 0.6,
			minWeight: 0.6,
			maxWeight: 1,
			maxStep: 8000,
			precision: 2,
		}
	}
	if (zoom <= 11) {
		return {
			radius: 16,
			opacity: 0.72,
			minWeight: 0.7,
			maxWeight: 1,
			maxStep: 3000,
			precision: 3,
		}
	}
	return {
		radius: 22,
		opacity: 0.82,
		minWeight: 0.8,
		maxWeight: 1,
		maxStep: 1000,
		precision: 3,
	}
}

const EARTH_RADIUS_M = 6371000
const toRadians = (value: number) => (value * Math.PI) / 180
const haversineMeters = (
	a: { latitude: number; longitude: number },
	b: { latitude: number; longitude: number }
) => {
	const lat1 = toRadians(a.latitude)
	const lat2 = toRadians(b.latitude)
	const dLat = lat2 - lat1
	const dLon = toRadians(b.longitude - a.longitude)
	const sinLat = Math.sin(dLat / 2)
	const sinLon = Math.sin(dLon / 2)
	const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * (sinLon * sinLon)
	return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

const densifyPoints = (
	points: { latitude: number; longitude: number }[],
	maxStepMeters: number
) => {
	const points0 = points[0]
	if (points.length <= 1 || points0 === undefined) {
		return points
	}
	const result: { latitude: number; longitude: number }[] = [points0]
	for (let i = 1; i < points.length; i += 1) {
		const prev = points[i - 1]
		const next = points[i]
		if (prev === undefined || next === undefined) continue
		const distance = haversineMeters(prev, next)
		const steps = Math.floor(distance / maxStepMeters)
		if (steps > 0) {
			for (let step = 1; step <= steps; step += 1) {
				const ratio = step / (steps + 1)
				result.push({
					latitude: prev.latitude + (next.latitude - prev.latitude) * ratio,
					longitude: prev.longitude + (next.longitude - prev.longitude) * ratio,
				})
			}
		}
		result.push(next)
	}
	return result
}

const buildHeatmapFeatures = (
	points: { latitude: number; longitude: number }[],
	maxStepMeters: number,
	precision: number,
	useCount: boolean
) => {
	const densified = densifyPoints(points, maxStepMeters)
	if (useCount === false) {
		return densified.map((pointData) => ({
			type: "Feature" as const,
			geometry: {
				type: "Point" as const,
				coordinates: [pointData.longitude, pointData.latitude],
			},
			properties: {
				count: 1,
			},
		}))
	}
	const counts = new Map<string, { latitude: number; longitude: number; count: number }>()
	for (const point of densified) {
		const roundedLatitude = Number(point.latitude.toFixed(precision))
		const roundedLongitude = Number(point.longitude.toFixed(precision))
		const key = `${roundedLatitude},${roundedLongitude}`
		const entry = counts.get(key)
		if (entry === undefined) {
			counts.set(key, {
				latitude: point.latitude,
				longitude: point.longitude,
				count: 1,
			})
			continue
		}
		entry.count += 1
	}
	return Array.from(counts.values()).map((pointData) => ({
		type: "Feature" as const,
		geometry: {
			type: "Point" as const,
			coordinates: [pointData.longitude, pointData.latitude],
		},
		properties: {
			count: pointData.count ?? 1,
		},
	}))
}

const getInterpolatedStopValue = (
	zoom: number,
	stops: HeatmapRadiusStop[] | undefined,
	fallbackValue: number,
	getStopValue: (stop: HeatmapRadiusStop) => number
) => {
	if (stops === undefined || stops.length === 0) {
		return fallbackValue
	}
	const sortedStops = [...stops]
		.filter((stop) => Number.isFinite(stop.zoom) && Number.isFinite(getStopValue(stop)))
		.sort((a, b) => a.zoom - b.zoom)

	const sortedStops0 = sortedStops[0]
	const sortedStopsLast = sortedStops[sortedStops.length - 1]
	if (sortedStops0 === undefined || sortedStopsLast === undefined) {
		return fallbackValue
	}
	const firstValue = getStopValue(sortedStops0)
	const lastValue = getStopValue(sortedStopsLast)
	if (zoom <= sortedStops0.zoom) {
		return firstValue
	}
	if (zoom >= sortedStopsLast.zoom) {
		return lastValue
	}
	for (let i = 1; i < sortedStops.length; i += 1) {
		const prev = sortedStops[i - 1]
		const next = sortedStops[i]
		if (prev !== undefined && zoom >= prev.zoom && next !== undefined && zoom <= next.zoom) {
			const prevValue = getStopValue(prev)
			const nextValue = getStopValue(next)
			const ratio = (zoom - prev.zoom) / (next.zoom - prev.zoom)
			return prevValue + (nextValue - prevValue) * ratio
		}
	}
	return fallbackValue
}

export const addVesselTrackHeatmap: DataSetter = (
	dispatch,
	queryClient,
	layerGroup,
	layerData,
	navigate,
	map
) => {
	if (layerGroup === undefined || map === undefined) return

	const typedLayerGroup = layerGroup as LayerGroupWithState
	const state = getOrCreateState(typedLayerGroup)
	state.requestId += 1
	const requestId = state.requestId

	const maptilerLayer = findMaptilerLayer(map)
	const maptilerMap = maptilerLayer?._maptilerMap

	if (state.zoomHandler !== undefined) {
		map.off("zoomend", state.zoomHandler)
		state.zoomHandler = undefined
	}

	state.currentHeatmaps?.forEach((refs) => {
		removeHeatmap(maptilerMap, refs)
	})
	state.currentHeatmaps = undefined

	const rawPointGroups = (
		layerData.vesselsTrackHeatmapData?.pointGroups ?? [
			layerData.vesselsTrackHeatmapData?.points ?? [],
		]
	)
		.flatMap((pointGroup) => splitTrackPointsAtMapEdges(pointGroup))
		.filter((pointGroup) => pointGroup.length > 0)
	const customOpacity = layerData.vesselsTrackHeatmapData?.opacity
	const customRadiusStops = layerData.vesselsTrackHeatmapData?.radiusStops
	const useCount = layerData.vesselsTrackHeatmapData?.useCount ?? true

	if (rawPointGroups.length === 0 || maptilerLayer === undefined) {
		state.lastRawPointGroups = undefined
		return
	}

	const addHeatmap = () => {
		if (state.requestId !== requestId) {
			return
		}
		const zoom = map.getZoom()
		const style = getHeatmapStyle(zoom)
		const radius = getInterpolatedStopValue(
			zoom,
			customRadiusStops,
			style.radius,
			(stop) => stop.radius
		)
		const opacity = customOpacity ?? style.opacity
		const minWeight = getInterpolatedStopValue(
			zoom,
			customRadiusStops,
			style.minWeight,
			(stop) => stop.minWeight
		)
		const maxWeight = getInterpolatedStopValue(
			zoom,
			customRadiusStops,
			style.maxWeight,
			(stop) => stop.maxWeight
		)
		const heatmapRefs = new Array<HeatmapRefs>()
		for (const rawPoints of rawPointGroups) {
			const points = buildHeatmapFeatures(rawPoints, style.maxStep, style.precision, useCount)
			const refs = maptilerLayer.addHeatmap?.({
				data: {
					...EMPTY_FEATURE_COLLECTION,
					features: points,
				},
				minzoom: 1,
				maxzoom: 18,
				opacity: opacity,
				radius: radius,
				...(useCount === true
					? {
							property: "count",
							weight: [
								{ propertyValue: 1, value: minWeight },
								{ propertyValue: 1500, value: maxWeight },
							],
						}
					: {}),
			})
			if (refs !== undefined) {
				heatmapRefs.push(refs)
			}
		}
		state.currentHeatmaps = heatmapRefs
	}

	state.lastRawPointGroups = rawPointGroups

	if (maptilerMap?.isStyleLoaded?.() === true) {
		addHeatmap()
		state.zoomHandler = () => {
			if (maptilerMap?.isStyleLoaded?.() !== true || state.lastRawPointGroups === undefined) {
				return
			}
			state.currentHeatmaps?.forEach((refs) => {
				removeHeatmap(maptilerMap, refs)
			})
			state.currentHeatmaps = undefined
			addHeatmap()
		}
		map.on("zoomend", state.zoomHandler)
		return
	}

	maptilerLayer.once("ready", addHeatmap)
}
54
